import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation } from "react-router-dom";
import { TAB_BRAND_ICON } from "../../config/constants";
import { LEGAL_DOC_HREFS } from "../../legal/legalDocRoutes";
import { logoutApi } from "../../service/user";
import { InitService } from "../../utils/initService";
import { globalStore } from "../../utils/globalStore";
import { getFirstHomeShellNavigationPath } from "../../utils/homeShellSidebar";
import {
  MARKETING_PAGE_PATH,
  PREMIUM_TRANSLATION_FULL_PATH,
} from "../../utils/menuPathNormalize";
import "./MarketingHeader.scss";

interface MarketingHeaderProps {
  edgeToEdge?: boolean;
}

const hasLoginToken = () => Boolean(localStorage.getItem("token"));

const MOBILE_BREAKPOINT = 850;

export const MarketingHeader: FC<MarketingHeaderProps> = ({
  edgeToEdge = false,
}) => {
  const [loggedIn, setLoggedIn] = useState(hasLoginToken);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  const hubHref = useMemo(() => {
    // 未登录用户跳转到登录页
    if (!loggedIn) return "/login";
    // 已登录用户跳转到 Lychee Hub 宣传页
    return "/lycheeHub";
  }, [loggedIn]);

  useEffect(() => {
    const syncLoginState = () => setLoggedIn(hasLoginToken());
    window.addEventListener("storage", syncLoginState);
    window.addEventListener("focus", syncLoginState);
    return () => {
      window.removeEventListener("storage", syncLoginState);
      window.removeEventListener("focus", syncLoginState);
    };
  }, []);

  const closeDrawer = useCallback(() => setDrawerOpen(false), []);
  const toggleDrawer = useCallback(() => setDrawerOpen((open) => !open), []);

  // 路由变化自动关闭抽屉
  useEffect(() => {
    closeDrawer();
  }, [location.pathname, closeDrawer]);

  // body 滚动锁
  useEffect(() => {
    if (!drawerOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [drawerOpen]);

  // ESC 关闭
  useEffect(() => {
    if (!drawerOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeDrawer();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [drawerOpen, closeDrawer]);

  // 视口变大（>850px）时强制关闭抽屉
  useEffect(() => {
    if (!drawerOpen) return;
    const onResize = () => {
      if (window.innerWidth > MOBILE_BREAKPOINT) closeDrawer();
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [drawerOpen, closeDrawer]);

  const handleLogout = () => {
    void logoutApi().catch(() => {});
    localStorage.removeItem("token");
    InitService.clearUserData();
    setLoggedIn(false);
    closeDrawer();
    window.location.replace(MARKETING_PAGE_PATH);
  };

  return (
    <header
      className={`nav marketing-header${edgeToEdge ? " marketing-header--edge" : ""}`}
    >
      <Link className="brand" to={MARKETING_PAGE_PATH}>
        <img
          className="brand-logo"
          src={TAB_BRAND_ICON}
          alt=""
          aria-hidden="true"
        />
        <span>Lychee Voice</span>
      </Link>
      <nav className="nav-links">
        <NavLink to={MARKETING_PAGE_PATH} end>
          {"首页"}
        </NavLink>
        <NavLink to="/lycheeHub">Lychee Hub</NavLink>
        <NavLink to="/agent" end>
          Lychee Agent
        </NavLink>
        <NavLink to="/api_doc" end>
          API & Skill
        </NavLink>
        <NavLink to="/pricing" end>
          {"价格"}
        </NavLink>
      </nav>
      <div className="nav-actions">
        {loggedIn ? (
          <button className="btn" type="button" onClick={handleLogout}>
            {"退出"}
          </button>
        ) : (
          <Link className="btn" to="/login">
            {"登录"}
          </Link>
        )}
        <button
          type="button"
          className="nav-menu-toggle"
          aria-label={drawerOpen ? "关闭菜单" : "打开菜单"}
          aria-expanded={drawerOpen}
          aria-controls="marketing-nav-drawer-panel"
          onClick={toggleDrawer}
        >
          <i className="fas fa-bars" aria-hidden="true" />
        </button>
      </div>
      {drawerOpen &&
        createPortal(
          <div
            className="marketing-nav-drawer-root"
            role="dialog"
            aria-label="导航菜单"
            aria-modal="true"
          >
            <div className="nav-drawer-overlay" onClick={closeDrawer} />
            <aside
              id="marketing-nav-drawer-panel"
              className="nav-drawer-panel"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="nav-drawer-header">
                <span className="nav-drawer-title">导航</span>
                <button
                  type="button"
                  className="nav-drawer-close"
                  aria-label="关闭菜单"
                  onClick={closeDrawer}
                >
                  <i className="fas fa-times" aria-hidden="true" />
                </button>
              </div>
              <nav className="nav-drawer-nav">
                <NavLink to={MARKETING_PAGE_PATH} end onClick={closeDrawer}>
                  {"首页"}
                </NavLink>
                <NavLink to="/lycheeHub" onClick={closeDrawer}>
                  Lychee Hub
                </NavLink>
                <NavLink to="/agent" end onClick={closeDrawer}>
                  Lychee Agent
                </NavLink>
                <NavLink to="/api_doc" end onClick={closeDrawer}>
                  Lychee API
                </NavLink>
                <NavLink to="/pricing" end onClick={closeDrawer}>
                  {"价格"}
                </NavLink>
              </nav>
            </aside>
          </div>,
          document.body,
        )}
    </header>
  );
};
